import React, { useState } from 'react';

const Services = () => {
  const [activeCategory, setActiveCategory] = useState('color');
  
  const serviceCategories = [
    {
      id: 'color',
      title: "Color",
      tagline: "Personalized Color Artistry",
      image: "/images/services/color.jpg",
      description: "Our colorists work one-on-one with you to incorporate your style, personality, skin tone and eye color to create a unique and personalized hair color that fits you perfectly.",
      note: "All color pricing depends on length and thickness of hair.",
      services: [
        { name: "Single Process with Glaze", duration: "90 mins" },
        { name: "Half Head Highlights", duration: "120 mins" },
        { name: "Full Head Highlights", duration: "150 mins" },
        { name: "Balayage / Foilayage", note: "BY CONSULTATION ONLY", duration: "180 mins" },
        { name: "Toner / Glaze without Glaze", duration: "45 mins" },
        { name: "Color Correction", note: "BY CONSULTATION ONLY", duration: "Varies" }
      ]
    },
    {
      id: 'treatment',
      title: "Treatment",
      tagline: "Luxurious Hair Therapy",
      image: "/images/services/treatment.jpg",
      description: "Keep your hair color vibrant, glowing with shine and feeling hydrated with a specialized in-salon conditioning treatment prescribed specifically for your hair's needs.",
      services: [
        { name: "Deep Conditioning with Blowout", duration: "60 mins" },
        { name: "Olaplex Treatment without Blowout", duration: "45 mins" },
        { name: "Keratin", duration: "180 mins" }
      ]
    },
    {
      id: 'styling',
      title: "Styling",
      tagline: "Precision Cuts & Styling",
      image: "/images/services/styling.jpg",
      description: "Enjoy a luxurious shampoo and professional styling. Then receive a customized haircut using scissor over comb to shape the best fit for your lifestyle needs.",
      services: [
        { name: "Haircut with Blowout", duration: "60 mins" },
        { name: "Children's Cut (12 and younger)", duration: "45 mins" },
        // { name: "Men's Cut", duration: "45 mins" },
        { name: "Blowout", duration: "45 mins" },
        { name: "Formal Styling", duration: "75 mins" },
        { name: "Updo", duration: "90 mins" }
      ]
    },
    {
      id: 'threading',
      title: "Threading",
      tagline: "Precise Beauty Enhancement",
      image: "/images/services/threading.jpg",
      description: "A glamorous new hair style that enhances your best facial features is made complete with our threading services.",
      services: [
        { name: "Eyebrows", duration: "15 mins" },
        { name: "Upper Lip", duration: "10 mins" },
        { name: "Sides", duration: "15 mins" },
        { name: "Chin", duration: "10 mins" },
        { name: "Special Eyebrow Tint", duration: "30 mins" }
      ]
    }
  ];

  return (
    <main className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative h-[30vh] sm:h-[35vh] lg:h-[40vh] flex items-center bg-[#F9F0F3] pt-16 lg:pt-20">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 right-0 w-full sm:w-1/2 h-full">
            <img
              src="/api/placeholder/1000/1000"
              alt="Luxury salon services"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-l from-transparent via-[#F9F0F3]/80 sm:via-transparent to-[#F9F0F3]" />
          </div>
        </div>
        
        <div className="container mx-auto px-4 sm:px-6 relative">
          <div className="max-w-xl lg:max-w-2xl">
            <span className="text-xs sm:text-sm uppercase tracking-widest text-[#A47C89] mb-2 sm:mb-4 block">
              Service Menu
            </span>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4 sm:mb-6">
              Elevate Your
              <span className="block font-playfair italic text-[#A47C89]">Natural Beauty</span>
            </h1>
            <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
              Discover our carefully curated selection of premium beauty services,
              each designed to enhance your unique style and natural features.
            </p>
          </div>
        </div>
      </section>

      {/* Services Navigation */}
      <section className="sticky top-0 bg-white z-50 border-b border-gray-100">
        <div className="container mx-auto px-4 sm:px-6">
          <div className="flex gap-6 sm:gap-8 overflow-x-auto hide-scrollbar py-4 sm:py-6">
            {serviceCategories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`whitespace-nowrap text-base sm:text-lg transition-colors ${
                  activeCategory === category.id
                    ? 'text-[#A47C89] font-medium'
                    : 'text-gray-400 hover:text-gray-600'
                }`}
              >
                {category.title}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Services Content */}
      <section className="py-12 sm:py-16 lg:py-20">
        {serviceCategories.map((category) => (
          <div
            key={category.id}
            className={`transition-all duration-500 ${
              activeCategory === category.id ? 'opacity-100' : 'hidden opacity-0'
            }`}
          >
            <div className="container mx-auto px-4 sm:px-6">
              {/* Category Header */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 mb-12 lg:mb-16">
                <div>
                  <span className="text-xs sm:text-sm uppercase tracking-widest text-[#A47C89] mb-3 sm:mb-4 block">
                    {category.tagline}
                  </span>
                  <h2 className="text-3xl sm:text-4xl font-playfair mb-4 sm:mb-6">{category.title}</h2>
                  <p className="text-base sm:text-lg text-gray-600 leading-relaxed">
                    {category.description}
                  </p>
                  {category.note && (
                    <p className="text-sm text-[#A47C89] italic mt-4">{category.note}</p>
                  )}
                </div>
                <div className="relative mt-6 lg:mt-0">
                  <img
                    src={category.image}
                    alt={category.title}
                    className="w-full h-[300px] sm:h-[400px] object-cover"
                  />
                  <div className="absolute -bottom-4 -left-4 w-24 sm:w-32 h-24 sm:h-32 bg-[#F9F0F3] rounded-full -z-10" />
                </div>
              </div>

              {/* Services Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 sm:gap-8">
                {category.services.map((service, index) => (
                  <div
                    key={index}
                    className="border-l-2 border-[#F9F0F3] pl-6 sm:pl-8 py-4 sm:py-6 group hover:border-[#A47C89] transition-colors"
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg sm:text-xl mb-2 group-hover:text-[#A47C89] transition-colors">
                          {service.name}
                        </h3>
                        {service.note && (
                          <span className="text-xs sm:text-sm text-[#A47C89] font-medium">
                            {service.note}
                          </span>
                        )}
                      </div>
                      <span className="text-xs sm:text-sm text-gray-400 group-hover:text-[#A47C89] transition-colors">
                        {service.duration}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Booking Call-to-Action */}
      <section className="bg-[#F9F0F3] py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 text-center">
          <h2 className="text-3xl sm:text-4xl font-playfair mb-4 sm:mb-6">Ready to Transform?</h2>
          <p className="text-base sm:text-lg text-gray-600 mb-6 sm:mb-8 max-w-xl sm:max-w-2xl mx-auto">
            Book your appointment today and experience the luxury of our premium services.
          </p>
          <a
            href="https://square.site/book/5CPJFK4WYVQF3/cut-n-curls-salon-manhasset-ny"
            className="inline-block bg-black text-white px-8 sm:px-10 py-3 sm:py-4 rounded-full hover:bg-gray-900 transition-colors"
          >
            Book Now
          </a>
        </div>
      </section>
    </main>
  );
};

export default Services;